import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonOrderedList,
  MenuButtonBulletedList,
  MenuButtonBlockquote,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  RichTextEditorProvider,
  RichTextField,
  MenuButtonAlignLeft,
  MenuButtonAlignCenter,
  MenuButtonAlignRight,
  MenuButtonAlignJustify,
  MenuButtonTextColor,
  MenuSelectFontFamily,
  MenuSelectFontSize,
  MenuButtonHighlightColor
} from 'mui-tiptap';
import { useEditor } from '@tiptap/react';

interface RichTextInputProps {
  initialHeight?: string;
  value: string;
  onChange: (value: string) => void;
  onResize?: (height: string) => void;
}

// Extensions definidas fora do componente para evitar recriação
const extensions = [
  StarterKit.configure({
    paragraph: { HTMLAttributes: {} }
    // StarterKit v3 já inclui Underline
  }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    alignments: ['left', 'center', 'right', 'justify'],
    defaultAlignment: 'left'
  }),
  // TextStyleKit v3 agrupa Color, FontFamily e FontSize
  TextStyleKit.configure({
    color: { types: ['textStyle'] },
    fontFamily: { types: ['textStyle'] },
    fontSize: { types: ['textStyle'] }
  }),
  Highlight.configure({ multicolor: true })
];

function RichTextInput({
  initialHeight = '300px',
  value,
  onChange,
  onResize
}: RichTextInputProps) {
  // Ref para rastrear se a mudança veio do próprio editor (evita loop infinito)
  const isInternalChange = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Flag para suprimir o ResizeObserver quando a altura é definida por mudança de prop
  // (evita loop: initialHeight muda → sx muda → ResizeObserver → onResize → estado muda → loop)
  const isProgrammaticResize = useRef(false);

  const editor = useEditor({
    extensions,
    content: value,
    // Em tiptap v3, habilitar para que os botões da toolbar reflitam o estado
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: { class: 'tiptap-editor' }
    },
    parseOptions: { preserveWhitespace: 'full' },
    onUpdate: ({ editor }) => {
      isInternalChange.current = true;
      onChange(editor.getHTML());
    },
    immediatelyRender: false
  });

  // Sincroniza mudanças externas de valor (ex: carregar imóvel, reset de form)
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    // Se a mudança veio do próprio editor, ignora para não criar loop
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    const currentHTML = editor.getHTML();
    if (value !== currentHTML) {
      // emitUpdate: false para não disparar onUpdate e criar loop
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [editor, value]);

  // Quando o prop initialHeight muda (ex: dados frescos do servidor), marca como resize
  // programático para que o ResizeObserver não dispare onResize e cause loop
  useEffect(() => {
    isProgrammaticResize.current = true;
    const t = setTimeout(() => {
      isProgrammaticResize.current = false;
    }, 100);
    return () => clearTimeout(t);
  }, [initialHeight]);

  // Observa o resize manual do container e notifica o parent
  useEffect(() => {
    if (!containerRef.current || !onResize) return;

    const observer = new ResizeObserver(([entry]) => {
      // Ignora redimensionamentos programáticos (mudança de prop, não ação do usuário)
      if (isProgrammaticResize.current) return;
      const newHeight = Math.round(entry.contentRect.height);
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = setTimeout(() => {
        onResize(`${newHeight}px`);
      }, 600);
    });

    observer.observe(containerRef.current);

    return () => {
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      observer.disconnect();
    };
  }, [onResize]);

  return (
    <RichTextEditorProvider editor={editor}>
      <Box
        ref={containerRef}
        sx={{
          resize: 'vertical',
          overflowX: 'hidden',
          overflowY: 'auto',
          minWidth: '300px',
          minHeight: '200px',
          height: initialHeight,
          width: '100%',
          maxWidth: '100%',
          border: '1px solid black',
          borderRadius: '10px',
          boxShadow: '0px 4px 3px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          '& .MuiTiptapEditor-root': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
          '& .MuiTiptapEditor-toolbar': {
            flexShrink: 0,
            borderBottom: '1px solid #e0e0e0'
          },
          '& .MuiTiptapEditor-content': {
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          },
          '& .ProseMirror': {
            flex: 1,
            outline: 'none',
            overflow: 'auto',
            height: '100%',
            maxHeight: 'none',
            paddingLeft: '8px'
          }
        }}>
        <RichTextField
          variant="standard"
          controls={
            <MenuControlsContainer>
              {/* Formatação de texto básica */}
              <MenuButtonBold />
              <MenuButtonItalic />
              <MenuButtonUnderline />
              <MenuDivider />

              {/* Alinhamento */}
              <MenuButtonAlignLeft />
              <MenuButtonAlignCenter />
              <MenuButtonAlignRight />
              <MenuButtonAlignJustify />
              <MenuDivider />

              {/* Títulos */}
              <MenuSelectHeading />
              <MenuDivider />

              {/* Listas */}
              <MenuButtonOrderedList />
              <MenuButtonBulletedList />
              <MenuDivider />

              {/* Quote */}
              <MenuButtonBlockquote />
              <MenuDivider />

              {/* Tipografia */}
              <MenuSelectFontFamily
                options={[
                  { label: 'Arial', value: 'Arial' },
                  { label: 'Comic Sans MS', value: 'Comic Sans MS' },
                  { label: 'Courier New', value: 'Courier New' },
                  { label: 'Georgia', value: 'Georgia' },
                  { label: 'Helvetica', value: 'Helvetica' },
                  { label: 'Impact', value: 'Impact' },
                  { label: 'Lucida Sans', value: 'Lucida Sans' },
                  { label: 'Tahoma', value: 'Tahoma' },
                  { label: 'Times New Roman', value: 'Times New Roman' },
                  { label: 'Trebuchet MS', value: 'Trebuchet MS' },
                  { label: 'Verdana', value: 'Verdana' }
                ]}
              />
              <MenuSelectFontSize
                options={[
                  { label: '8', value: '8pt' },
                  { label: '9', value: '9pt' },
                  { label: '10', value: '10pt' },
                  { label: '11', value: '11pt' },
                  { label: '12', value: '12pt' },
                  { label: '14', value: '14pt' },
                  { label: '16', value: '16pt' },
                  { label: '18', value: '18pt' },
                  { label: '20', value: '20pt' },
                  { label: '22', value: '22pt' },
                  { label: '24', value: '24pt' },
                  { label: '26', value: '26pt' },
                  { label: '28', value: '28pt' },
                  { label: '36', value: '36pt' },
                  { label: '48', value: '48pt' },
                  { label: '72', value: '72pt' }
                ]}
              />
              <MenuDivider />

              {/* Cores */}
              <MenuButtonTextColor />
              <MenuButtonHighlightColor />
            </MenuControlsContainer>
          }
          sx={{
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'column',
            '& .MuiTiptap-FieldContainer-notchedOutline': {
              border: 'none'
            }
          }}
        />
      </Box>
    </RichTextEditorProvider>
  );
}

const MemoizedRichTextInput = React.memo(RichTextInput);

export default MemoizedRichTextInput;
